/**
 * What narrows and orders the Walks list (design: "Led AI Console", the WALKS
 * screen's chip row).
 *
 * ## Two of the artboard's three chips, and why not the third
 *
 * The design draws "All time", "Anyone" and "Any sync state". The first two
 * are here. The third is not: a walk carries no integration state — see
 * `ConsoleWalkResponse`, which has a status, a creator and three counts and
 * nothing about Procore or Autodesk — and the chip would have had to invent
 * one. What a walk DOES carry about its downstream life is whether its
 * records have been reviewed, and the list already has chips for that.
 *
 * ## Order is over DAYS, not over walks
 *
 * The list groups by calendar day, and every useful ordering of walks —
 * biggest first, longest first — cuts across those groups and would have to
 * dissolve them. So the sort turns the day stack around instead, which is the
 * one ordering the grouping can express, and within a day capture order
 * stands.
 */

import type { ConsoleWalkResponse } from "../api";

export type WalkRange = "all" | "today" | "week" | "month";
export type WalkOrder = "newest" | "oldest";

export const WALK_RANGE_LABEL: Record<WalkRange, string> = {
  all: "All time",
  today: "Today",
  week: "Last 7 days",
  month: "Last 30 days",
};

export const WALK_ORDER_LABEL: Record<WalkOrder, string> = {
  newest: "Newest first",
  oldest: "Oldest first",
};

/** Local midnight, so a range is a run of CALENDAR days rather than a
 *  multiple of 24 hours measured from whenever the page was opened. */
function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function inWalkRange(
  walk: ConsoleWalkResponse,
  range: WalkRange,
  now: Date,
): boolean {
  if (range === "all") return true;
  const started = Date.parse(walk.startedAt);
  // An unparseable stamp is KEPT. A walk that cannot say when it happened is
  // still a walk, and hiding it behind a date filter would make it
  // unreachable from the one page that lists walks.
  if (!Number.isFinite(started)) return true;
  const days = range === "today" ? 0 : range === "week" ? 6 : 29;
  const floor = startOfDay(now).getTime() - days * 86_400_000;
  return started >= floor;
}

/**
 * Every walker in the list, for the "Anyone" menu — by the label the rows
 * themselves print, so the menu and the list cannot disagree about who
 * somebody is.
 */
export function walkerOptions(
  walks: ConsoleWalkResponse[],
  labelOf: (walk: ConsoleWalkResponse) => string,
): Array<{ value: string; count: number }> {
  const counts = new Map<string, number>();
  for (const walk of walks) {
    const label = labelOf(walk);
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([value, count]) => ({ value, count }))
    .sort((left, right) => right.count - left.count || left.value.localeCompare(right.value));
}
